/**
 * Tilläggsavtal om tidsbestämd hyra (tillaggsavtal_tidsbestamd_hyra v1).
 *
 * Supplement to a bostadshyresavtal (also studentbostad) fixing a stepped
 * rent: the rent is adjusted on a set day each year by a stated percentage,
 * and the amount in kronor per step is authoritative. Legal text verbatim
 * from `base-templates/Tillaggsavtal_tidsbestamd_hyra.docx` (Haymanot-approved).
 * DO NOT paraphrase or reorder.
 */
import { p, section, t, table, val } from '@hyranai/regta-document-ir';
import { customSectionsBlockDefinition, fieldsSection, formatSlotNumber, partyDetailsBlock, propertySlot, referenceLine, shown, signingBlockDefinition, slotNumber, slotText, textVal, } from '../common/index.js';
import { rows } from '../lib.js';
const K = 'tillaggsavtal_tidsbestamd_hyra';
const LEGAL = {
    plane: 'legal',
    status: 'published',
    language: 'sv-SE',
    legalReview: { reviewedBy: 'Haymanot', source: 'base-templates/Tillaggsavtal_tidsbestamd_hyra.docx' },
};
/** Tilläggsavtalets egna (valfria) avtalsnummer, centred under the heading. */
export const avtalsnummer = {
    blockKey: `${K}.avtalsnummer`,
    version: 'v1',
    plane: 'shared',
    status: 'published',
    language: 'sv-SE',
    title: 'Avtalsnummer',
    summary: 'The supplement’s own optional reference number; hidden when empty.',
    slots: [{ key: 'contract_number', label: 'Avtalsnummer', type: 'text', help: 'Valfritt — egen referens på tilläggsavtalet.' }],
    compile: (input, ctx) => shown(ctx, slotText(input.contract_number) != null)
        ? [{ ...referenceLine([['Avtalsnummer', textVal(input, 'contract_number')]]), align: 'center' }]
        : null,
};
export const hyresvard = partyDetailsBlock({
    blockKey: `${K}.hyresvard`,
    ref: 'hyresvard',
    title: 'Hyresvärd',
    summary: 'The landlord: name and organisation number.',
    role: 'lessor',
    fields: [
        { key: 'landlord_name', label: 'Namn', field: 'name', required: true },
        { key: 'landlord_org_number', label: 'Organisationsnummer', field: 'legalId' },
    ],
});
export const hyresgast = partyDetailsBlock({
    blockKey: `${K}.hyresgast`,
    ref: 'hyresgast',
    title: 'Hyresgäst',
    summary: 'The tenant and, when present, the co-tenant: name and personal/organisation number.',
    groups: [
        {
            role: 'lessee',
            ordinal: 0,
            fields: [
                { key: 'tenant_name', label: 'Namn', field: 'name', required: true },
                { key: 'tenant_id_number', label: 'Personnr / Org.nr', field: 'legalId' },
            ],
        },
        {
            role: 'lessee',
            ordinal: 1,
            subheading: 'Medhyresgäst',
            showWhenAnyOf: ['cotenant_name'],
            fields: [
                { key: 'cotenant_name', label: 'Namn (medhyresgäst)', field: 'name' },
                { key: 'cotenant_id_number', label: 'Personnr / Org.nr (medhyresgäst)', field: 'legalId' },
            ],
        },
    ],
});
const FROM_PROPERTY = 'Förifylls från hyresavtalets fastighet.';
export const hyresavtalet = fieldsSection({
    blockKey: `${K}.hyresavtalet`,
    ref: 'hyresavtalet',
    title: 'Hyresavtalet',
    summary: 'The bostadshyresavtal the supplement belongs to: contract number, property and apartment.',
    rows: [
        {
            slot: {
                key: 'parent_contract_number',
                label: 'Hyreskontrakt nr',
                type: 'text',
                required: true,
                derivedFrom: 'contract_number',
                help: 'Numret på det bostadshyresavtal som tillägget avser. Förifylls från avtalet.',
            },
        },
        { slot: propertySlot('property_designation', 'Fastighetsbeteckning', 'designation', { required: true, help: FROM_PROPERTY }) },
        { slot: propertySlot('property_municipality', 'Kommun', 'municipality', { help: FROM_PROPERTY }) },
        { slot: propertySlot('property_address', 'Postadress', 'address', { type: 'textarea', help: FROM_PROPERTY }) },
        {
            slot: {
                key: 'apartment_official_number',
                label: 'Officiellt lägenhetsnummer',
                type: 'text',
                derivedFrom: 'property_official_apt_no',
                help: 'Förifylls från hyresavtalet.',
            },
        },
    ],
});
const readSchedule = (value) => rows(value).map((row) => ({
    date: typeof row.date === 'string' ? row.date : '',
    percent: slotNumber(row.percent),
    amount: slotNumber(row.amount),
}));
/**
 * The stored kronor amount wins ("kronorbeloppet har företräde"); a row
 * without one is computed cumulatively from the previous amount (or the
 * move-in rent) × (1 + percent/100), rounded — as in the legacy template.
 */
export function computeSchedule(base, schedule) {
    let running = base;
    return schedule.map((r) => {
        let amount = r.amount;
        if (amount == null && running != null && r.percent != null)
            amount = Math.round(running * (1 + r.percent / 100));
        running = amount ?? running;
        return { ...r, amount };
    });
}
const withUnit = (ctx, n, unit, sourceKey) => {
    const text = formatSlotNumber(ctx, n);
    return text == null
        ? [val(null, { sourceKey, blankWidth: 'short' }), t(` ${unit}`)]
        : [val(`${text} ${unit}`, { raw: n, format: 'number', sourceKey })];
};
export const tidsbestamdHyra = {
    ...LEGAL,
    blockKey: `${K}.tidsbestamd-hyra`,
    version: 'v1',
    title: 'Tidsbestämd hyra',
    summary: 'The move-in rent, the yearly adjustment day and period, and the stepped rent table with the kronor amount prevailing.',
    slots: [
        {
            key: 'move_in_rent',
            label: 'Hyra vid inflyttningen (kr/månad)',
            type: 'currency',
            required: true,
            derivedFrom: 'rent_amount',
            help: 'Inflyttningshyran. Hämtas från hyresavtalet — utgångspunkt för beräkningen.',
        },
        { key: 'period_start', label: 'Period från och med', type: 'date', required: true },
        { key: 'period_end', label: 'Period till och med', type: 'date', required: true },
        { key: 'adjustment_day', label: 'Hyran justeras den', type: 'text', default: '1 januari', help: 'Vilket datum varje år hyran justeras, t.ex. "1 januari".' },
        {
            key: 'schedule',
            label: 'Hyrestrappa',
            type: 'table',
            repeats: true,
            help: 'En rad per justering: datum, höjning i procent och beloppsbestämd månadshyra. Sätt procent så fylls kronor i automatiskt — eller tvärtom. Båda går att justera; kronorbeloppet har företräde.',
            columns: [
                { key: 'date', label: 'Datum då hyran gäller', type: 'date' },
                { key: 'percent', label: 'Höjningsprocent', type: 'number' },
                { key: 'amount', label: 'Beloppsbestämd månadshyra (kr)', type: 'currency' },
            ],
        },
    ],
    compile: (input, ctx) => {
        const base = slotNumber(input.move_in_rent);
        const computed = computeSchedule(base, readSchedule(input.schedule));
        const scheduleRows = ctx.mode === 'blank' || computed.length === 0 ? [{ date: '', percent: null, amount: null }] : computed;
        const baseText = formatSlotNumber(ctx, base);
        const content = [
            p([
                t('Den avtalade hyran uppgår vid inflyttningen till '),
                val(baseText, { raw: base, format: 'number', sourceKey: 'move_in_rent', blankWidth: 'short' }),
                t(' kronor per månad.'),
            ]),
            p([
                t('Parterna är överens om att hyran ska justeras den '),
                textVal(input, 'adjustment_day', 'short'),
                t(' varje år under perioden från och med '),
                textVal(input, 'period_start'),
                t(' till och med '),
                textVal(input, 'period_end'),
                t(' enligt nedanstående tabell.'),
            ]),
            table(['Datum då hyran gäller', 'Höjningsprocent', 'Beloppsbestämd månadshyra (kr)'], scheduleRows.map((row) => [
                [val(row.date || null, { format: 'date', sourceKey: 'schedule', blankWidth: 'medium' })],
                withUnit(ctx, row.percent, '%', 'schedule'),
                withUnit(ctx, row.amount, 'kr/månad', 'schedule'),
            ]), { variant: 'data', repeatTableHeader: true }),
            p('Beräkningen har gjorts med utgångspunkt i inflyttningshyran och angiven höjningsprocent. Den hyra som gäller för respektive period är den beloppsbestämda månadshyra i kronor som anges i tabellen.'),
            p('Vid eventuell avvikelse mellan angiven höjningsprocent och angiven månadshyra i kronor ska den beloppsbestämda månadshyran i kronor ha företräde.'),
            p('Parterna är överens om att hyran under hyresförhållandet ska utvecklas enligt de principer som framgår av detta tilläggsavtal. Parternas gemensamma avsikt är att även efter avtalsperiodens utgång träffa en ny överenskommelse om tidsbestämd hyra baserad på motsvarande modell och struktur, om inte väsentligt förändrade marknadsförhållanden, lagstiftning eller andra särskilda omständigheter motiverar annat.'),
        ];
        return [section('tidsbestamd-hyra', 'Tidsbestämd hyra', content)];
    },
};
export const customSections = customSectionsBlockDefinition(`${K}.custom-sections`);
export const underskrifter = signingBlockDefinition({ blockKey: `${K}.underskrifter` });
export const TILLAGGSAVTAL_TIDSBESTAMD_HYRA_BLOCKS = [
    avtalsnummer,
    hyresvard,
    hyresgast,
    hyresavtalet,
    tidsbestamdHyra,
    customSections,
    underskrifter,
];
//# sourceMappingURL=blocks.js.map